import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { ProductCurrencyType } from '@/entities/product/types';
import type { BookedActivity, BookedRoom, BookedSlot } from '@/entities/room/types';

// 정렬 함수들
const sortBookedRoomsByDate = (rooms: BookedRoom[]): BookedRoom[] => {
  return [...rooms].sort((a, b) => {
    if (!a.checkIn && !b.checkIn) return 0;
    if (!a.checkIn) return 1;
    if (!b.checkIn) return -1;
    return new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime();
  });
};

const sortBookedActivitiesByDate = (activities: BookedActivity[]): BookedActivity[] => {
  return [...activities].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
};

const sortBookedSlotsByDate = (slots: BookedSlot[]): BookedSlot[] => {
  return [...slots].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
};

interface ProductStore {
  currentProductIdx: string | null;
  currency: ProductCurrencyType;
  bookedRooms: BookedRoom[];
  bookedActivities: BookedActivity[];
  bookedSlots: BookedSlot[];
  setCurrentProductIdx: (productId: string) => void;
  setCurrency: (currency: ProductCurrencyType) => void;
  setBookedRooms: (rooms: BookedRoom[] | ((prev: BookedRoom[]) => BookedRoom[])) => void;
  setBookedActivities: (
    activities: BookedActivity[] | ((prev: BookedActivity[]) => BookedActivity[]),
  ) => void;
  setBookedSlots: (slots: BookedSlot[] | ((prev: BookedSlot[]) => BookedSlot[])) => void;
  resetStore: () => void;
}

export const useProductStore = create<ProductStore>()(
  persist(
    set => ({
      currentProductIdx: null,
      currency: 'KRW',
      bookedRooms: [],
      bookedActivities: [],
      bookedSlots: [],
      setCurrentProductIdx: (productIdx: string) =>
        set(state => {
          // 다른 productId가 들어오면 모든 데이터 초기화
          if (state.currentProductIdx && state.currentProductIdx !== productIdx) {
            return {
              currentProductIdx: productIdx,
              currency: 'KRW',
              bookedRooms: [],
              bookedActivities: [],
              bookedSlots: [],
            };
          }
          return {
            ...state,
            currentProductIdx: productIdx,
          };
        }),
      setCurrency: (currency: ProductCurrencyType) => set(state => ({ ...state, currency })),
      setBookedRooms: (rooms: BookedRoom[] | ((prev: BookedRoom[]) => BookedRoom[])) =>
        set(state => {
          const newRooms = typeof rooms === 'function' ? rooms(state.bookedRooms) : rooms;
          return {
            bookedRooms: sortBookedRoomsByDate(newRooms),
          };
        }),
      setBookedActivities: (
        activities: BookedActivity[] | ((prev: BookedActivity[]) => BookedActivity[]),
      ) =>
        set(state => {
          const newActivities =
            typeof activities === 'function' ? activities(state.bookedActivities) : activities;
          return {
            bookedActivities: sortBookedActivitiesByDate(newActivities),
          };
        }),
      setBookedSlots: (slots: BookedSlot[] | ((prev: BookedSlot[]) => BookedSlot[])) =>
        set(state => {
          const newSlots = typeof slots === 'function' ? slots(state.bookedSlots) : slots;
          return {
            bookedSlots: sortBookedSlotsByDate(newSlots),
          };
        }),
      resetStore: () =>
        set(() => ({
          bookedRooms: [],
          bookedActivities: [],
          bookedSlots: [],
        })),
    }),
    {
      name: 'product-store',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
